import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../UI/Button';
import {
  ClassificationSchema,
  ClassificationField,
  ClassificationOption,
} from '../../types/classificationSchema';
import { agentBotService } from '../../services/agentBotService';
import { useToast } from '../UI/ToastProvider';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { helpdeskRoutes } from '../../routes/helpdeskRoutes';

interface AgentBotClassificationEditorProps {
  botId: string;
}

type FieldDraft = {
  id: string;
  code: string;
  title: string;
  description: string;
  required: boolean;
  showInConversation: boolean;
  isActive: boolean;
};

type OptionDraft = {
  id: string;
  fieldId: string;
  slug: string;
  name: string;
  criteria: string;
  color: string;
  isActive: boolean;
};

const emptyFieldDraft = (): FieldDraft => ({
  id: '',
  code: '',
  title: '',
  description: '',
  required: false,
  showInConversation: true,
  isActive: true,
});

const emptyOptionDraft = (fieldId = ''): OptionDraft => ({
  id: '',
  fieldId,
  slug: '',
  name: '',
  criteria: '',
  color: '#6b7280',
  isActive: true,
});

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
  {
    return crypto.randomUUID();
  }

  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const AgentBotClassificationEditor: React.FC<AgentBotClassificationEditorProps> = ({
  botId,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schema, setSchema] = useState<ClassificationSchema | null>(null);
  const [expandedFieldId, setExpandedFieldId] = useState<string | null>(null);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<FieldDraft | null>(null);
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  const [editingOption, setEditingOption] = useState<OptionDraft | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setExpandedFieldId(null);
    setEditingFieldId(null);
    setEditingField(null);
    setEditingOptionId(null);
    setEditingOption(null);
    setIsDirty(false);
    void loadSchema(botId);
  }, [botId]);

  const loadSchema = async (botId: string) => {
    setLoading(true);
    try
    {
      const data = await agentBotService.getClassificationSchema(botId);
      const sorted: ClassificationSchema = {
        fields: [...data.fields]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((field) => ({
            ...field,
            options: [...field.options].sort((a, b) => a.sortOrder - b.sortOrder),
          })),
      };
      setSchema(sorted);
      if (sorted.fields.length > 0)
      {
        setExpandedFieldId(sorted.fields[0].id);
      }
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao carregar schema de classificação'), 'error');
      setSchema(null);
    }
    finally
    {
      setLoading(false);
    }
  };

  const markDirty = (next: ClassificationSchema) => {
    setSchema(next);
    setIsDirty(true);
  };

  const updateField = (fieldId: string, patch: Partial<ClassificationField>) => {
    if (!schema)
    {
      return;
    }

    markDirty({
      fields: schema.fields.map((field) =>
        field.id === fieldId ? { ...field, ...patch } : field
      ),
    });
  };

  const updateOption = (
    fieldId: string,
    optionId: string,
    patch: Partial<ClassificationOption>
  ) => {
    if (!schema)
    {
      return;
    }

    markDirty({
      fields: schema.fields.map((field) => {
        if (field.id !== fieldId)
        {
          return field;
        }

        return {
          ...field,
          options: field.options.map((option) =>
            option.id === optionId ? { ...option, ...patch } : option
          ),
        };
      }),
    });
  };

  const openCreateField = () => {
    const newId = 'new-field';
    setEditingFieldId(newId);
    setEditingField(emptyFieldDraft());
    setEditingOptionId(null);
    setEditingOption(null);
    setExpandedFieldId(newId);
  };

  const openEditField = (field: ClassificationField) => {
    setEditingFieldId(field.id);
    setEditingField({
      id: field.id,
      code: field.code,
      title: field.title,
      description: field.description ?? '',
      required: field.required,
      showInConversation: field.showInConversation,
      isActive: field.isActive,
    });
    setEditingOptionId(null);
    setEditingOption(null);
    setExpandedFieldId(field.id);
  };

  const openCreateOption = (fieldId: string) => {
    const newId = `new-option-${fieldId}`;
    setEditingOptionId(newId);
    setEditingOption(emptyOptionDraft(fieldId));
    setEditingFieldId(null);
    setEditingField(null);
    setExpandedFieldId(fieldId);
  };

  const openEditOption = (fieldId: string, option: ClassificationOption) => {
    setEditingOptionId(option.id);
    setEditingOption({
      id: option.id,
      fieldId,
      slug: option.slug,
      name: option.name,
      criteria: option.criteria ?? '',
      color: option.color ?? '#6b7280',
      isActive: option.isActive,
    });
    setEditingFieldId(null);
    setEditingField(null);
    setExpandedFieldId(fieldId);
  };

  const saveFieldDraft = () => {
    if (!schema || !editingField)
    {
      return;
    }

    const title = editingField.title.trim();
    let code = editingField.code.trim().toLowerCase();
    if (!code)
    {
      code = slugify(title);
    }

    if (!title || !code)
    {
      addToast('Informe título e código do campo', 'error');
      return;
    }

    const duplicated = schema.fields.some(
      (field) => field.code === code && field.id !== editingField.id
    );
    if (duplicated)
    {
      addToast('Já existe um campo com este código', 'error');
      return;
    }

    if (!editingField.id)
    {
      const newField: ClassificationField = {
        id: createId(),
        code,
        title,
        description: editingField.description.trim() || null,
        type: 'enum',
        sortOrder: schema.fields.length + 1,
        required: editingField.required,
        showInConversation: editingField.showInConversation,
        isActive: editingField.isActive,
        options: [],
      };
      markDirty({ fields: [...schema.fields, newField] });
      setExpandedFieldId(newField.id);
      addToast('Campo adicionado', 'success');
    }
    else
    {
      updateField(editingField.id, {
        code,
        title,
        description: editingField.description.trim() || null,
        required: editingField.required,
        showInConversation: editingField.showInConversation,
        isActive: editingField.isActive,
      });
      addToast('Campo atualizado', 'success');
    }

    setEditingFieldId(null);
    setEditingField(null);
  };

  const saveOptionDraft = () => {
    if (!schema || !editingOption)
    {
      return;
    }

    const name = editingOption.name.trim();
    let slug = editingOption.slug.trim().toLowerCase();
    if (!slug)
    {
      slug = slugify(name);
    }

    if (!name || !slug)
    {
      addToast('Informe nome e slug da opção', 'error');
      return;
    }

    const field = schema.fields.find((item) => item.id === editingOption.fieldId);
    if (!field)
    {
      addToast('Campo não encontrado', 'error');
      return;
    }

    const duplicated = field.options.some(
      (option) => option.slug === slug && option.id !== editingOption.id
    );
    if (duplicated)
    {
      addToast('Já existe uma opção com este slug neste campo', 'error');
      return;
    }

    if (!editingOption.id)
    {
      const newOption: ClassificationOption = {
        id: createId(),
        slug,
        name,
        criteria: editingOption.criteria.trim() || null,
        color: editingOption.color || '#6b7280',
        sortOrder: field.options.length + 1,
        isActive: editingOption.isActive,
      };
      markDirty({
        fields: schema.fields.map((item) =>
          item.id === field.id
            ? { ...item, options: [...item.options, newOption] }
            : item
        ),
      });
      addToast('Opção adicionada', 'success');
    }
    else
    {
      updateOption(editingOption.fieldId, editingOption.id, {
        slug,
        name,
        criteria: editingOption.criteria.trim() || null,
        color: editingOption.color || '#6b7280',
        isActive: editingOption.isActive,
      });
      addToast('Opção atualizada', 'success');
    }

    setEditingOptionId(null);
    setEditingOption(null);
  };

  const removeField = (fieldId: string) => {
    if (!schema)
    {
      return;
    }

    const confirmed = window.confirm('Remover este campo e todas as opções?');
    if (!confirmed)
    {
      return;
    }

    markDirty({
      fields: schema.fields
        .filter((field) => field.id !== fieldId)
        .map((field, index) => ({ ...field, sortOrder: index + 1 })),
    });
    if (expandedFieldId === fieldId)
    {
      setExpandedFieldId(null);
    }
    if (editingFieldId === fieldId)
    {
      setEditingFieldId(null);
      setEditingField(null);
    }
    if (editingOption?.fieldId === fieldId)
    {
      setEditingOptionId(null);
      setEditingOption(null);
    }
    addToast('Campo removido', 'info');
  };

  const removeOption = (fieldId: string, optionId: string) => {
    if (!schema)
    {
      return;
    }

    const confirmed = window.confirm('Remover esta opção?');
    if (!confirmed)
    {
      return;
    }

    markDirty({
      fields: schema.fields.map((field) => {
        if (field.id !== fieldId)
        {
          return field;
        }

        return {
          ...field,
          options: field.options
            .filter((option) => option.id !== optionId)
            .map((option, index) => ({ ...option, sortOrder: index + 1 })),
        };
      }),
    });
    if (editingOptionId === optionId)
    {
      setEditingOptionId(null);
      setEditingOption(null);
    }
    addToast('Opção removida', 'info');
  };

  const handleSave = async () => {
    if (!schema)
    {
      return;
    }

    setSaving(true);
    try
    {
      const persisted = await agentBotService.updateClassificationSchema(botId, schema);
      const sorted: ClassificationSchema = {
        fields: [...persisted.fields]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((field) => ({
            ...field,
            options: [...field.options].sort((a, b) => a.sortOrder - b.sortOrder),
          })),
      };
      setSchema(sorted);
      setIsDirty(false);
      addToast('Schema de classificação salvo', 'success');
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao salvar schema de classificação'), 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    const confirmed = window.confirm(
      'Restaurar o schema padrão (campo lead com as 5 temperaturas)? As alterações atuais serão substituídas.'
    );
    if (!confirmed)
    {
      return;
    }

    setSaving(true);
    try
    {
      const persisted = await agentBotService.resetClassificationSchema(botId);
      const sorted: ClassificationSchema = {
        fields: [...persisted.fields]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((field) => ({
            ...field,
            options: [...field.options].sort((a, b) => a.sortOrder - b.sortOrder),
          })),
      };
      setSchema(sorted);
      setEditingFieldId(null);
      setEditingField(null);
      setEditingOptionId(null);
      setEditingOption(null);
      setIsDirty(false);
      if (sorted.fields.length > 0)
      {
        setExpandedFieldId(sorted.fields[0].id);
      }
      addToast('Schema restaurado para o padrão', 'success');
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao restaurar o schema padrão'), 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  const renderFieldEditor = () => {
    if (!editingField)
    {
      return null;
    }

    const isNew = !editingField.id;

    return (
      <div className="p-4 space-y-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-1 w-1 rounded-full bg-blue-500"></div>
          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            {isNew ? 'Novo campo' : 'Editando campo'}
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Título *</label>
            <input
              type="text"
              value={editingField.title}
              onChange={(e) => {
                const title = e.target.value;
                setEditingField((prev) => {
                  if (!prev)
                  {
                    return prev;
                  }

                  const next = { ...prev, title };
                  if (!prev.id && !prev.code)
                  {
                    next.code = slugify(title);
                  }
                  return next;
                });
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex.: Temperatura do Lead"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Código *</label>
            <input
              type="text"
              value={editingField.code}
              onChange={(e) =>
                setEditingField((prev) =>
                  prev ? { ...prev, code: e.target.value.toLowerCase() } : prev
                )
              }
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="ex.: lead"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Descrição (instrução geral para a IA)
          </label>
          <textarea
            value={editingField.description}
            onChange={(e) =>
              setEditingField((prev) =>
                prev ? { ...prev, description: e.target.value } : prev
              )
            }
            rows={2}
            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="Como a IA deve interpretar este atributo..."
          />
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
          <label className="inline-flex items-center gap-1.5 cursor-pointer hover:text-gray-900">
            <input
              type="checkbox"
              checked={editingField.required}
              onChange={(e) =>
                setEditingField((prev) =>
                  prev ? { ...prev, required: e.target.checked } : prev
                )
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Obrigatório
          </label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer hover:text-gray-900">
            <input
              type="checkbox"
              checked={editingField.showInConversation}
              onChange={(e) =>
                setEditingField((prev) =>
                  prev ? { ...prev, showInConversation: e.target.checked } : prev
                )
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Exibir na conversa
          </label>
          <label className="inline-flex items-center gap-1.5 cursor-pointer hover:text-gray-900">
            <input
              type="checkbox"
              checked={editingField.isActive}
              onChange={(e) =>
                setEditingField((prev) =>
                  prev ? { ...prev, isActive: e.target.checked } : prev
                )
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Ativo
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => {
              setEditingFieldId(null);
              setEditingField(null);
            }}
          >
            Cancelar
          </Button>
          <Button variant="primary" size="sm" type="button" onClick={saveFieldDraft}>
            {isNew ? 'Adicionar' : 'Salvar'}
          </Button>
        </div>
      </div>
    );
  };

  const renderOptionEditor = (fieldId: string) => {
    if (!editingOption || editingOption.fieldId !== fieldId)
    {
      return null;
    }

    const isNew = !editingOption.id;
    const parentField = schema?.fields.find((field) => field.id === editingOption.fieldId);

    return (
      <div className="p-3 space-y-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-1 w-1 rounded-full bg-indigo-500"></div>
          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            {isNew ? 'Nova opção' : 'Editando opção'}
            {parentField && (
              <span className="ml-2 text-xs font-normal text-gray-500 normal-case">
                em {parentField.title}
              </span>
            )}
          </h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nome *</label>
            <input
              type="text"
              value={editingOption.name}
              onChange={(e) => {
                const name = e.target.value;
                setEditingOption((prev) => {
                  if (!prev)
                  {
                    return prev;
                  }

                  const next = { ...prev, name };
                  if (!prev.id && !prev.slug)
                  {
                    next.slug = slugify(name);
                  }
                  return next;
                });
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex.: Quente"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Slug *</label>
            <input
              type="text"
              value={editingOption.slug}
              onChange={(e) =>
                setEditingOption((prev) =>
                  prev ? { ...prev, slug: e.target.value.toLowerCase() } : prev
                )
              }
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="ex.: lead-quente"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Cor</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={editingOption.color || '#6b7280'}
              onChange={(e) =>
                setEditingOption((prev) =>
                  prev ? { ...prev, color: e.target.value } : prev
                )
              }
              className="h-8 w-10 border border-gray-300 rounded cursor-pointer bg-white"
            />
            <input
              type="text"
              value={editingOption.color}
              onChange={(e) =>
                setEditingOption((prev) =>
                  prev ? { ...prev, color: e.target.value } : prev
                )
              }
              className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Critério para IA
          </label>
          <textarea
            value={editingOption.criteria}
            onChange={(e) =>
              setEditingOption((prev) =>
                prev ? { ...prev, criteria: e.target.value } : prev
              )
            }
            rows={2}
            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            placeholder="Quando a IA deve escolher esta opção..."
          />
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer hover:text-gray-900">
          <input
            type="checkbox"
            checked={editingOption.isActive}
            onChange={(e) =>
              setEditingOption((prev) =>
                prev ? { ...prev, isActive: e.target.checked } : prev
              )
            }
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Ativo
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => {
              setEditingOptionId(null);
              setEditingOption(null);
            }}
          >
            Cancelar
          </Button>
          <Button variant="primary" size="sm" type="button" onClick={saveOptionDraft}>
            {isNew ? 'Adicionar' : 'Salvar'}
          </Button>
        </div>
      </div>
    );
  };

  const renderFieldCard = (field: ClassificationField) => {
    const isExpanded = expandedFieldId === field.id;
    const isEditing = editingFieldId === field.id;

    return (
      <div key={field.id} className="rounded-lg border border-gray-200 overflow-hidden">
        {/* Header com toggle e botões de ação */}
        <div className="flex items-stretch bg-gray-50">
          <button
            type="button"
            onClick={() => {
              if (isEditing)
              {
                return;
              }
              setExpandedFieldId(isExpanded ? null : field.id);
              if (editingOption && editingOption.fieldId !== field.id)
              {
                setEditingOptionId(null);
                setEditingOption(null);
              }
            }}
            className={`flex-1 flex items-center justify-between gap-3 p-4 transition-colors text-left ${
              isEditing ? 'cursor-default' : 'hover:bg-gray-100'
            }`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{field.title}</span>
                <span className="text-xs text-gray-500 font-mono bg-gray-200 px-1.5 py-0.5 rounded">
                  {field.code}
                </span>
                {field.required && (
                  <span className="text-xs text-red-600 font-medium">obrigatório</span>
                )}
                <span
                  className={`inline-flex px-2 py-0.5 text-xs rounded-full border ${
                    field.isActive
                      ? 'bg-green-50 text-green-700 border-green-100'
                      : 'bg-gray-50 text-gray-500 border-gray-200'
                  }`}
                >
                  {field.isActive ? 'Ativo' : 'Inativo'}
                </span>
              </div>
              {field.description && (
                <p className="mt-1 text-xs text-gray-600 line-clamp-1">{field.description}</p>
              )}
            </div>
            {!isEditing && (
              <svg
                className={`h-5 w-5 text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>
          {!isEditing && (
            <div className="flex items-center gap-1 pr-3">
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => openEditField(field)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </Button>
              <Button
                variant="danger"
                size="sm"
                type="button"
                onClick={() => removeField(field.id)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </div>
          )}
        </div>

        {/* Formulário de edição inline do campo */}
        {isEditing && renderFieldEditor()}

        {/* Lista de opções (só aparece quando não está editando o campo) */}
        {!isEditing && isExpanded && (
          <div className="p-4 space-y-3 border-t border-gray-200">
            <div className="text-xs text-gray-500">
              {field.options.length} opção(ões) configurada(s)
            </div>

            <div className="space-y-2">
              {field.options.map((option) => {
                const isEditingOption = editingOptionId === option.id;
                return (
                  <div
                    key={option.id}
                    className="rounded-md border border-gray-200 overflow-hidden"
                  >
                    {!isEditingOption ? (
                      <div className="p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className="inline-block h-3 w-3 rounded-full shrink-0 border border-gray-300"
                                style={{ backgroundColor: option.color ?? '#6b7280' }}
                              />
                              <span className="text-sm font-medium text-gray-900">{option.name}</span>
                              <span className="text-xs text-gray-500 font-mono">{option.slug}</span>
                              {!option.isActive && (
                                <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                                  Inativo
                                </span>
                              )}
                            </div>
                            {option.criteria && (
                              <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                                {option.criteria}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="secondary"
                              size="sm"
                              type="button"
                              onClick={() => openEditOption(field.id, option)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              type="button"
                              onClick={() => removeOption(field.id, option.id)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      renderOptionEditor(field.id)
                    )}
                  </div>
                );
              })}
            </div>

            <div className="pt-1">
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => openCreateOption(field.id)}
                disabled={editingOptionId !== null}
              >
                + Adicionar opção
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <p className="text-sm text-gray-600">
        Defina os atributos que a IA deve extrair no encerramento da conversa.
        Pode ser qualquer coisa: temperatura do lead, segmento de mercado, produto de interesse...
      </p>

      <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
        Alertas para a equipe (Discord/e-mail) não se configuram aqui.
        Use{' '}
        <Link to={helpdeskRoutes.settingsInboxes} className="font-semibold underline">
          Caixas de entrada → Notificações externas
        </Link>{' '}
        para escolher o canal e quais classificações (e eventos operacionais) disparam cada canal.
      </div>

      {isDirty && (
        <p className="text-xs text-amber-700">Há alterações locais ainda não salvas.</p>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : schema ? (
        <div className="space-y-3">
          {/* Card de criação de novo campo */}
          {editingFieldId === 'new-field' && (
            <div className="rounded-lg border border-gray-300 bg-white shadow-sm overflow-hidden">
              {renderFieldEditor()}
            </div>
          )}

          {/* Cards de campos existentes */}
          {schema.fields.map(renderFieldCard)}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 text-sm">
          Nenhum campo configurado.
        </div>
      )}

      <div className="flex flex-wrap justify-between gap-2 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={openCreateField}
            disabled={saving || editingFieldId !== null || editingOptionId !== null}
          >
            + Adicionar campo
          </Button>
          <Button
            variant="secondary"
            type="button"
            onClick={() => void handleResetDefaults()}
            disabled={saving || editingFieldId !== null || editingOptionId !== null}
          >
            Restaurar padrão
          </Button>
        </div>
        <Button
          variant="primary"
          type="button"
          onClick={() => void handleSave()}
          disabled={!schema || saving || editingFieldId !== null || editingOptionId !== null}
        >
          {saving ? 'Salvando...' : 'Salvar classificação'}
        </Button>
      </div>
    </div>
  );
};
